import { fromBase64, toBase64 } from './atoms/convertors'
import { Hashing } from './atoms/hashing'
import { Symmetric } from './atoms/symmetric'
import { getCurrentEncryptionVersion } from './end2end/login'
import { prehashPassword } from './password'
import { createRecoveryKey, createSystemRecoveryKey } from './recovery'
import { DERIVED_KEY_LENGTH } from './utils/constants'
import { getSodium } from './utils/general'

const keypairSeed = new Uint8Array([
  114, 226, 201, 4, 80, 146, 117, 179, 36, 9, 78, 168, 172, 99, 33, 171, 149, 77, 175, 116, 31, 154,
  32, 131, 164, 183, 37, 165, 176, 235, 62, 95,
])

/**
 * FOR TESTING PURPOSES ONLY,
 * absolutely insecure
 */

const defaultPassword = 'FaceUp4444'

export const mockUserKeys = async (
  APP_SPECIFIC_SALT: string,
  SYSTEM_PUBLIC_KEY: string,
  password = defaultPassword,
  shouldGenerateRecovery = true
) => {
  const { publicKey, privateKey } = await generateDeterministicKeypair()

  const salt = await Hashing.generateSalt()

  const prehashedPassword = await prehashPassword({
    password,
    salt: await toBase64(salt),
    version: getCurrentEncryptionVersion() as 1 | 2,
  })

  if (prehashedPassword.isErr()) {
    console.error('prehashedPassword:', prehashedPassword.error.message)
    return
  }

  const { passwordKey, passwordKeyPrehash } = prehashedPassword.value
  const passwordKeyAppPrehash = await Hashing.deriveHashKey(
    DERIVED_KEY_LENGTH,
    Hashing.toPassword(await fromBase64(passwordKeyPrehash)),
    Hashing.toSalt(await fromBase64(APP_SPECIFIC_SALT))
  )

  if (passwordKeyAppPrehash.isErr()) {
    console.error('passwordKeyAppPrehash:', passwordKeyAppPrehash.error.message)
    return
  }

  const passwordHash = await Hashing.hashForStorage(Hashing.toPassword(passwordKeyAppPrehash.value))
  if (passwordHash.isErr()) {
    console.error('passwordHash:', passwordHash.error.message)
    return
  }

  const nonce = await Symmetric.generateNonce()
  const userEncryptedPrivateKey = await Symmetric.encrypt(
    privateKey,
    Symmetric.toKey(await fromBase64(passwordKey)),
    nonce
  )

  if (userEncryptedPrivateKey.isErr()) {
    console.error('userEncryptedPrivateKey:', userEncryptedPrivateKey.error.message)
    return
  }

  const recoveryPayload = await createRecoveryKey(
    await toBase64(userEncryptedPrivateKey.value),
    await toBase64(nonce),
    passwordKey
  )

  if (recoveryPayload.isErr()) {
    console.error('recoveryPayload:', recoveryPayload.error.message)
    return
  }

  const systemRecoveryKey = await createSystemRecoveryKey(
    recoveryPayload.value.recoveryKeyPlain,
    SYSTEM_PUBLIC_KEY
  )

  if (systemRecoveryKey.isErr()) {
    console.error('systemRecoveryKey:', systemRecoveryKey)
    return
  }

  return {
    public_key: await toBase64(publicKey),
    private_key: await toBase64(userEncryptedPrivateKey.value),
    nonce: await toBase64(nonce),
    salt: await toBase64(salt),
    password: passwordHash.value,

    ...(shouldGenerateRecovery && {
      private_key_recovery: recoveryPayload.value.privateKeyRecovery ?? undefined,
      private_key_recovery_nonce: recoveryPayload.value.privateKeyRecoveryNonce ?? undefined,
      recovery_key_encrypted: recoveryPayload.value.recoveryKeyEncrypted ?? undefined,
      recovery_key_encrypted_nonce: recoveryPayload.value.recoveryKeyEncryptedNonce ?? undefined,
      recovery_key_system_encrypted: systemRecoveryKey.value ?? undefined,
    }),
  }
}

const generateDeterministicKeypair = async () => {
  const sodium = await getSodium()
  const { publicKey, privateKey } = sodium.crypto_box_seed_keypair(keypairSeed)

  return { publicKey, privateKey }
}
